import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { openProfile } from '../lib/nav';
import Loader from './Loader';

/* Network map (beta): every member, and how they're tied together. Admins
   get the full view (admin prop); members get theirs, without anything
   private to others, with themselves highlighted. A small force layout computed once in the browser — at B/N's
   size that's instant and needs no graph library. */

const W = 1000;
const H = 640;
const KINDS = [
  ['connection', 'Connections', '#4DA3FF'],
  ['invite', 'Invited', '#F5B83D'],
  ['intro', 'Introductions waiting', '#B58CFF'],
  ['group', 'Share a group', '#3DD6A3'],
];
const COLOR = Object.fromEntries(KINDS.map(([k, , c]) => [k, c]));

function layout(nodes, edges) {
  const pos = {};
  const n = nodes.length || 1;
  nodes.forEach((node, i) => {
    const a = (2 * Math.PI * i) / n;
    pos[node.id] = { x: W / 2 + Math.cos(a) * 220, y: H / 2 + Math.sin(a) * 200, vx: 0, vy: 0 };
  });
  const links = edges.filter((e) => e.kind !== 'group');
  // Fewer passes as the network grows, so the page stays quick.
  const steps = n > 150 ? 60 : 400;
  for (let step = 0; step < steps; step += 1) {
    const cool = 1 - step / steps;
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = pos[nodes[i].id];
        const b = pos[nodes[j].id];
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        const d2 = Math.max(dx * dx + dy * dy, 25);
        const f = 5200 / d2;
        const d = Math.sqrt(d2);
        dx /= d; dy /= d;
        a.vx += dx * f; a.vy += dy * f; b.vx -= dx * f; b.vy -= dy * f;
      }
    }
    links.forEach((e) => {
      const a = pos[e.source];
      const b = pos[e.target];
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const f = (d - 120) * 0.02;
      a.vx += (dx / d) * f; a.vy += (dy / d) * f; b.vx -= (dx / d) * f; b.vy -= (dy / d) * f;
    });
    nodes.forEach((node) => {
      const p = pos[node.id];
      p.vx += (W / 2 - p.x) * 0.004;
      p.vy += (H / 2 - p.y) * 0.004;
      p.x = Math.min(W - 40, Math.max(40, p.x + p.vx * cool));
      p.y = Math.min(H - 40, Math.max(40, p.y + p.vy * cool));
      p.vx *= 0.6; p.vy *= 0.6;
    });
  }
  // Fit the drawing to the frame, so a small network isn't a speck in the middle.
  const xs = nodes.map((node) => pos[node.id].x);
  const ys = nodes.map((node) => pos[node.id].y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const k = Math.min((W - 140) / Math.max(x1 - x0, 1), (H - 120) / Math.max(y1 - y0, 1), 3);
  nodes.forEach((node) => {
    const p = pos[node.id];
    p.x = W / 2 + (p.x - (x0 + x1) / 2) * k;
    p.y = H / 2 + (p.y - (y0 + y1) / 2) * k;
  });
  return pos;
}

const initials = (name) => (name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

export default function NetworkMap({ notify, admin = false }) {
  const [data, setData] = useState(null);
  const [show, setShow] = useState({ connection: true, invite: true, intro: true, group: false });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    (admin ? api.adminNetwork() : api.network()).then(setData).catch((e) => notify?.(e.message));
  }, [notify, admin]);

  const pos = useMemo(() => (data ? layout(data.nodes, data.edges) : {}), [data]);
  if (!data) return <Loader label="Drawing the network…" />;

  const edges = data.edges.filter((e) => show[e.kind]);
  const near = hover ? new Set(edges.filter((e) => e.source === hover || e.target === hover).flatMap((e) => [e.source, e.target])) : null;
  const radius = (node) => 16 + Math.min(node.connections, 10) * 2;
  const s = data.stats;

  return (
    <div className="netmap">
      <section className="card pad">
        <div className="netmap-head">
          <h2 className="ad-h">Network map <em className="beta-tag">Beta</em></h2>
          <div className="netmap-keys">
            {KINDS.map(([k, label, color]) => (
              <label key={k} className="netmap-key">
                <input type="checkbox" checked={show[k]} onChange={() => setShow({ ...show, [k]: !show[k] })} />
                <i style={{ background: color }} /> {label}
              </label>
            ))}
          </div>
        </div>
        <svg className="netmap-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Map of members and how they're connected">
          <defs>
            {data.nodes.map((n) => (
              <clipPath key={n.id} id={`nm-${n.id}`}><circle cx={pos[n.id].x} cy={pos[n.id].y} r={radius(n) - 2} /></clipPath>
            ))}
          </defs>
          {edges.map((e, i) => {
            const a = pos[e.source];
            const b = pos[e.target];
            const lit = !hover || e.source === hover || e.target === hover;
            return (
              <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={COLOR[e.kind]}
                strokeWidth={e.kind === 'connection' ? 2.5 : 1.6} strokeDasharray={e.kind === 'intro' ? '6 5' : e.kind === 'group' ? '2 4' : undefined}
                opacity={lit ? 0.85 : 0.08}>
                <title>{e.kind === 'group' ? `Share: ${(e.groups || []).join(', ')}` : e.kind}</title>
              </line>
            );
          })}
          {data.nodes.map((n) => {
            const p = pos[n.id];
            const r = radius(n);
            const dim = near && !near.has(n.id) && hover !== n.id;
            return (
              <g key={n.id} className="netmap-node" opacity={dim ? 0.2 : 1} onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                onClick={() => openProfile(n.handle)} tabIndex={0} role="button" aria-label={`${n.name}, ${n.connections} connections`}
                onKeyDown={(e) => { if (e.key === 'Enter') openProfile(n.handle); }}>
                {n.id === s.you && <circle cx={p.x} cy={p.y} r={r + 7} className="netmap-you" />}
                <circle cx={p.x} cy={p.y} r={r} className={`netmap-dot${n.admin ? ' admin' : ''}${n.connections === 0 ? ' alone' : ''}`} />
                {n.avatar_url ? (
                  <image href={n.avatar_url} x={p.x - r} y={p.y - r} width={r * 2} height={r * 2} clipPath={`url(#nm-${n.id})`} preserveAspectRatio="xMidYMid slice" />
                ) : (
                  <text x={p.x} y={p.y + 4} textAnchor="middle" className="netmap-initials">{initials(n.name)}</text>
                )}
                <text x={p.x} y={p.y + r + 14} textAnchor="middle" className="netmap-name">{n.id === s.you ? 'You' : n.name.split(' ')[0]}</text>
                <title>{`${n.name}${n.headline ? ` — ${n.headline}` : ''}\n${n.connections} connection${n.connections === 1 ? '' : 's'}`}</title>
              </g>
            );
          })}
        </svg>
        <p className="muted-sm">Hover a member to light up their ties; click to open their profile. Bigger dots have more connections; gold rings are admins.
          {!admin && ' Introductions and groups show only when you’re part of them.'}</p>
      </section>

      <section className="card pad">
        <h2 className="ad-h">What it says</h2>
        <div className="ad-grid">
          {!admin && <div className="ad-stat"><b>{s.your_connections}</b><span>your connections</span></div>}
          {!admin && <div className="ad-stat"><b>{s.friends_of_friends}</b><span>people one step away — ask for an intro</span></div>}
          <div className="ad-stat"><b>{s.members}</b><span>members</span></div>
          <div className="ad-stat"><b>{s.connections}</b><span>connections</span></div>
          <div className="ad-stat"><b>{s.pending_intros}</b><span>{admin ? 'introductions waiting' : 'your introductions waiting'}</span></div>
          <div className="ad-stat"><b>{s.clusters}</b><span>separate clusters</span></div>
        </div>
        <div className="netmap-lists">
          {admin && <div>
            <h3 className="card-title">No connections yet — introduce them</h3>
            {s.unconnected.length === 0 ? <p className="muted-sm">Everyone has at least one.</p> : (
              <ul>{s.unconnected.map((m) => <li key={m.id}>{m.name}</li>)}</ul>
            )}
          </div>}
          <div>
            <h3 className="card-title">Most connected</h3>
            <ul>{s.most_connected.map((m) => <li key={m.id}>{m.name} · {m.connections}</li>)}</ul>
          </div>
        </div>
      </section>
    </div>
  );
}
